import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, ExternalLink, Clock } from "lucide-react";
import { dict } from "../i18n";
import { DISEASE_ARTICLES, pickArticleLang } from "../data/diseaseArticles";
import { articleSlug } from "../data/diseaseSlugs";
import SEO from "../components/SEO";
import { pagePath } from "../lib/routes";

// 10 EXTERNAL articles from kilpnäärmekliinik.ee blog (provided by client)
const EXTERNAL_IMG_BASE = "/api/static/external-articles";
const EXTERNAL_ARTICLES = [
  {
    id: "hashimoto",
    title: {
      et: "Hashimoto türeoidiit: sümptomid, põhjused, diagnoosimine ja ravi",
      ru: "Тиреоидит Хашимото: симптомы, причины, диагностика и лечение",
      en: "Hashimoto's thyroiditis: symptoms, causes, diagnosis and treatment",
    },
    url: "https://kilpnäärmekliinik.ee/blog/hashimoto-tureoidiit",
    image: `${EXTERNAL_IMG_BASE}/hashimotos-thyroiditis-autoimmune.webp`,
  },
  {
    id: "graves",
    title: {
      et: "Gravesi tõbi: sümptomid, põhjused, diagnoosimine ja ravi",
      ru: "Болезнь Грейвса: симптомы, причины, диагностика и лечение",
      en: "Graves' disease: symptoms, causes, diagnosis and treatment",
    },
    url: "https://kilpnäärmekliinik.ee/blog/gravesi-tobi",
    image: `${EXTERNAL_IMG_BASE}/graves-disease.webp`,
  },
  {
    id: "hypothyroidism",
    title: {
      et: "Hüpotüreoidism: mis seda põhjustab, sümptomid, kuidas diagnoosida ja ravida",
      ru: "Гипотиреоз: причины, симптомы, диагностика и лечение",
      en: "Hypothyroidism: causes, symptoms, how to diagnose and treat",
    },
    url: "https://kilpnäärmekliinik.ee/blog/kilpnaarme-alatalitlus",
    image: `${EXTERNAL_IMG_BASE}/hypothyroidism.webp`,
  },
  {
    id: "hyperthyroidism",
    title: {
      et: "Hüpertüreoidism: mis seda põhjustab, millised on sümptomid, kuidas seda diagnoosida ning ravida",
      ru: "Гипертиреоз: причины, симптомы, диагностика и лечение",
      en: "Hyperthyroidism: causes, symptoms, how to diagnose and treat",
    },
    url: "https://kilpnäärmekliinik.ee/blog/kilpnaarme-uletalitlus",
    image: `${EXTERNAL_IMG_BASE}/hyperthyroidism.webp`,
  },
  {
    id: "subclinical",
    title: {
      et: "Kas peaksite saama ravi subkliinilise hüpotüreoidismi korral?",
      ru: "Нужно ли лечить субклинический гипотиреоз?",
      en: "Should you get treatment for subclinical hypothyroidism?",
    },
    url: "https://kilpnäärmekliinik.ee/blog/subkliiniline-hupotureoidism",
    image: `${EXTERNAL_IMG_BASE}/subclinical-hypothyroidism.webp`,
  },
  {
    id: "nodules",
    title: {
      et: "Kilpnäärme sõlmed: hindamine, TIRADS ja biopsia",
      ru: "Узлы щитовидной железы: оценка, TIRADS и биопсия",
      en: "Thyroid nodules: evaluation, TIRADS and biopsy",
    },
    url: "https://kilpnäärmekliinik.ee/blog/kilpnaarme-solmed",
    image: `${EXTERNAL_IMG_BASE}/thyroid-nodules.webp`,
  },
  {
    id: "cancer",
    title: {
      et: "Kilpnäärmevähk: liigid, kuidas seda ära tunda ja kuidas seda tänapäeval ravida",
      ru: "Рак щитовидной железы: виды, как распознать и как лечить сегодня",
      en: "Thyroid cancer: types, how to recognise it and how it is treated today",
    },
    url: "https://kilpnäärmekliinik.ee/blog/kilpnaarmevahk",
    image: `${EXTERNAL_IMG_BASE}/thyroid-cancer.webp`,
  },
  {
    id: "pregnancy",
    title: {
      et: "Kilpnääre ja rasedus: analüüsid, eesmärgid ja jälgimine",
      ru: "Щитовидная железа и беременность: анализы, цели и наблюдение",
      en: "Thyroid and pregnancy: tests, targets and monitoring",
    },
    url: "https://kilpnäärmekliinik.ee/blog/kilpnaare-ja-rasedus",
    image: `${EXTERNAL_IMG_BASE}/thyroid-and-pregnancy.webp`,
  },
  {
    id: "postpartum",
    title: {
      et: "Sünnitusjärgne türeoidiit: diagnoosimine ja ravi",
      ru: "Послеродовой тиреоидит: диагностика и лечение",
      en: "Postpartum thyroiditis: diagnosis and treatment",
    },
    url: "https://kilpnäärmekliinik.ee/blog/sunnitusjargne-tureoidiit",
    image: `${EXTERNAL_IMG_BASE}/postpartum-thyroiditis.webp`,
  },
  {
    id: "ultrasound",
    title: {
      et: "Mida kilpnäärme ultraheliuuring ja TIRADS-i vastus tegelikult tähendab?",
      ru: "Что на самом деле означает УЗИ щитовидной железы и заключение TIRADS?",
      en: "What does a thyroid ultrasound and TIRADS report actually mean?",
    },
    url: "https://kilpnäärmekliinik.ee/blog/kilpnaarme-ultraheli-tirads",
    image: `${EXTERNAL_IMG_BASE}/thyroid-ultrasound-tirads-guide.webp`,
  },
];

export default function Diseases({ lang }) {
  const t = dict[lang];
  const p = t.diseasesPage;

  return (
    <>
      <SEO lang={lang} page="diseases" />
      <section className="container-page pt-12 pb-8" data-testid="diseases-page">
        <div className="chip mb-3">{p.tag}</div>
        <h1 className="section-title">{p.title}</h1>
        <p className="text-surface-inkSoft mt-3 text-[15px] max-w-2xl">{p.subtitle}</p>
      </section>

      {/* LOCAL ARTICLES (open on this site) */}
      <section className="container-page pb-10">
        <h2 className="text-2xl sm:text-3xl font-semibold text-surface-ink mb-2">
          {p.diabetesTitle}
        </h2>
        <p className="text-surface-inkSoft text-[14px] max-w-2xl mb-6">
          {p.diabetesSubtitle}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {DISEASE_ARTICLES.map((articleRaw) => {
            const a = pickArticleLang(articleRaw, lang);
            return (
            <Link
              key={a.slug}
              to={`${pagePath("diseases", lang)}/${articleSlug(a.slug, lang)}`}
              data-testid={`disease-local-${a.slug}`}
              className="card-clinic group flex items-start gap-4 hover:border-sapphire-300 hover:shadow-ambientLg transition-all"
            >
              {a.image ? (
                <div className="h-20 w-20 rounded-xl overflow-hidden shrink-0 bg-surface-canvas border border-surface-outline/30">
                  <img
                    src={a.image}
                    alt={`${a.title}/Tallinna Endokrinoloogia Kliinik`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              ) : (
                <div className="h-12 w-12 rounded-xl flex items-center justify-center shrink-0 bg-sapphire-50 text-sapphire-700 group-hover:bg-sapphire-700 group-hover:text-white transition-colors">
                  <BookOpen className="h-6 w-6" />
                </div>
              )}
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-teal-500">
                    {a.category}
                  </span>
                  <span className="text-[11px] text-surface-inkSoft inline-flex items-center gap-1">
                    <Clock className="h-3 w-3" /> {a.readingMinutes} min
                  </span>
                </div>
                <h2 className="text-[17px] font-semibold text-surface-ink leading-snug">
                  {a.title}
                </h2>
                <p className="text-[13px] text-surface-inkSoft mt-2 line-clamp-2 leading-relaxed">
                  {a.summary}
                </p>
                <span className="mt-3 inline-flex items-center gap-1.5 text-[14px] font-medium text-sapphire-700 group-hover:gap-2.5 transition-all">
                  {lang === "et"
                    ? "Loe artiklit"
                    : lang === "ru"
                    ? "Читать статью"
                    : "Read article"}{" "}
                  <ArrowRight className="h-3.5 w-3.5" />
                </span>
              </div>
            </Link>
            );
          })}
        </div>
      </section>

      {/* EXTERNAL — kilpnäärmekliinik.ee */}
      <section className="container-page pb-16">
        <h2 className="text-2xl sm:text-3xl font-semibold text-surface-ink mb-2 inline-flex items-center gap-2">
          {p.thyroidTitle}
          <ExternalLink className="h-4 w-4 text-teal-500" />
        </h2>
        <p className="text-surface-inkSoft text-[14px] max-w-2xl mb-6">
          {p.thyroidSubtitle}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {EXTERNAL_ARTICLES.map((a, idx) => (
            <a
              key={a.id}
              href={a.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`disease-article-${a.id}`}
              className="card-clinic group flex items-start gap-4 hover:border-sapphire-300 hover:shadow-ambientLg transition-all"
            >
              {a.image ? (
                <div className="h-20 w-20 rounded-xl overflow-hidden shrink-0 bg-surface-canvas border border-surface-outline/30">
                  <img
                    src={a.image}
                    alt={`${a.title[lang]}/Tallinna Endokrinoloogia Kliinik`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              ) : (
                <div className="h-12 w-12 rounded-xl flex items-center justify-center shrink-0 bg-teal-200/50 text-teal-500 group-hover:bg-sapphire-700 group-hover:text-white transition-colors">
                  <BookOpen className="h-6 w-6" />
                </div>
              )}
              <div className="flex-1">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-surface-inkSoft mb-1">
                  {String(idx + 1).padStart(2, "0")}
                </div>
                <h2 className="text-[17px] font-semibold text-surface-ink leading-snug">
                  {a.title[lang]}
                </h2>
                <span className="mt-3 inline-flex items-center gap-1.5 text-[14px] font-medium text-sapphire-700 group-hover:gap-2.5 transition-all">
                  {p.readCta} <ArrowRight className="h-3.5 w-3.5" />
                </span>
              </div>
            </a>
          ))}
        </div>
      </section>
    </>
  );
}
